import { useEffect, useMemo, useState } from "react";
import { Plus, Archive, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Pill, EmptyState } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";
export const PRICING_TYPES = [
  ["flat", "Flat fee"],
  ["hourly", "Per hour"],
  ["daily", "Per day"],
  ["per_item", "Per item"],
  ["percentage", "Percentage of selections"],
];
const CATEGORIES = ["Design Services", "Project Management", "Procurement", "Labor & Installation",
  "Logistics", "Fabrication", "Styling", "Rental", "Materials", "Expenses", "Other"];
const blank = {
  name: "", description: "", category: "Design Services", default_rate: "", pricing_type: "flat",
  default_quantity: 1, unit: "", taxable: true, internal_cost: "", status: "Active",
};

export default function InvoiceLibrary() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/invoice-library", { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(
    () =>
      items.filter(
        (i) =>
          (!category || i.category === category) &&
          (!query || `${i.name} ${i.description || ""}`.toLowerCase().includes(query.toLowerCase()))
      ),
    [items, query, category]
  );

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Give the item a name.");
      return;
    }
    setBusy(true);
    const payload = {
      ...editing,
      default_rate: editing.default_rate === "" ? null : Number(editing.default_rate),
      internal_cost: editing.internal_cost === "" ? null : Number(editing.internal_cost),
      default_quantity: Number(editing.default_quantity) || 1,
      description: editing.description || null,
      unit: editing.unit || null,
    };
    try {
      if (editing.id) {
        delete payload.id;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload.visibility;
        delete payload.vendor_id;
        await api.patch(`/invoice-library/${editing.id}`, payload);
        toast.success("Item updated — existing invoices keep their own pricing");
      } else {
        await api.post("/invoice-library", payload);
        toast.success(`${payload.name} added to your library`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (item) => {
    if (!window.confirm(`Archive “${item.name}”? Past invoices keep it.`)) return;
    try {
      await api.delete(`/invoice-library/${item.id}`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-10 pb-10" data-testid="invoice-library-page">
      <PageHeader
        overline="Billing"
        title="Invoice Item Library"
        subtitle="Reusable services and charges. Prices here are defaults only — every invoice keeps its own."
        actions={
          isAdmin && (
            <button
              type="button"
              data-testid="new-library-item-btn"
              onClick={() => setEditing({ ...blank })}
              className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New Item
            </button>
          )
        }
      />

      <div className="flex flex-wrap gap-3">
        <input
          data-testid="library-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items…"
          className={`${input} sm:max-w-xs`}
        />
        <select data-testid="library-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className={`${input} sm:max-w-xs`}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-stone">
          <input type="checkbox" data-testid="library-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
      </div>

      {shown.length === 0 ? (
        <EmptyState title="No invoice items yet" subtitle="Add a design fee, installation labour or any charge you bill often." />
      ) : (
        <div className="card-surface divide-y divide-line" data-testid="library-list">
          {shown.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-5" data-testid={`library-item-${item.id}`}>
              <div className="min-w-[12rem] flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-ink">{item.name}</span>
                  {item.category && <Pill tone="bg-line text-stone">{item.category}</Pill>}
                  {item.status === "Archived" && <Pill tone="bg-[#F6DED6] text-[#A8492E]">Archived</Pill>}
                </div>
                {item.description && <p className="mt-1 text-xs leading-relaxed text-stone">{item.description}</p>}
              </div>
              <div className="text-right">
                <div className="num text-ink">
                  {item.default_rate === null || item.default_rate === undefined
                    ? "Price on use"
                    : item.pricing_type === "percentage"
                    ? `${item.default_rate}%`
                    : money(item.default_rate)}
                </div>
                <div className="text-xs text-fog">
                  {(PRICING_TYPES.find(([v]) => v === item.pricing_type) || [])[1]}
                  {item.unit ? ` · ${item.unit}` : ""}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <button type="button" data-testid={`library-edit-${item.id}`} onClick={() => setEditing({ ...item, default_rate: item.default_rate ?? "", internal_cost: item.internal_cost ?? "" })} className="rounded-sm border border-line p-2 text-stone hover:border-lime hover:text-ink">
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  {item.status !== "Archived" && (
                    <button type="button" data-testid={`library-archive-${item.id}`} onClick={() => archive(item)} className="rounded-sm border border-line p-2 text-stone hover:border-clay hover:text-clay">
                      <Archive className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="library-item-dialog">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <span className="overline">Invoice item</span>
                <h3 className="mt-2 font-serif text-2xl text-ink">{editing.id ? "Edit item" : "New item"}</h3>
              </div>
              <button type="button" data-testid="library-dialog-close" onClick={() => setEditing(null)} className="rounded-sm border border-line p-1.5 text-stone hover:border-fog" aria-label="Close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Item Name *" className="sm:col-span-2">
                <input data-testid="library-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={input} />
              </Field>
              <Field label="Default Description" className="sm:col-span-2">
                <textarea data-testid="library-description-input" rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={input} />
              </Field>
              <Field label="Category">
                <select data-testid="library-category-input" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={input}>
                  <option value="">— None —</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Pricing Type">
                <select data-testid="library-pricing-input" value={editing.pricing_type} onChange={(e) => setEditing({ ...editing, pricing_type: e.target.value })} className={input}>
                  {PRICING_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label={editing.pricing_type === "percentage" ? "Default Percentage" : "Default Rate"} hint="Leave blank to enter a price each time">
                <input data-testid="library-rate-input" type="number" step="0.01" min="0" value={editing.default_rate} onChange={(e) => setEditing({ ...editing, default_rate: e.target.value })} className={`${input} num`} />
              </Field>
              <Field label="Default Quantity">
                <input data-testid="library-quantity-input" type="number" step="0.5" min="0" value={editing.default_quantity} onChange={(e) => setEditing({ ...editing, default_quantity: e.target.value })} className={`${input} num`} />
              </Field>
              <Field label="Unit (hours, days, each…)">
                <input data-testid="library-unit-input" value={editing.unit || ""} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} className={input} />
              </Field>
              <Field label="Internal Cost (optional)">
                <input data-testid="library-cost-input" type="number" step="0.01" min="0" value={editing.internal_cost} onChange={(e) => setEditing({ ...editing, internal_cost: e.target.value })} className={`${input} num`} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-stone sm:col-span-2">
                <input type="checkbox" data-testid="library-taxable-input" checked={editing.taxable} onChange={(e) => setEditing({ ...editing, taxable: e.target.checked })} />
                Taxable by default
              </label>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" data-testid="library-save-btn" disabled={busy} onClick={save} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
                {busy ? "Saving…" : "Save Item"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
